import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { useUser } from '@/hooks/useAuth'
import type { Notification } from '@/types'

export function useNotifications() {
  const user = useUser()
  const queryClient = useQueryClient()

  const query = useQuery<Notification[]>({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(20)
      if (error) throw error
      return data ?? []
    },
    enabled: !!user,
    staleTime: 30_000,
  })

  // Realtime: push new rows into the cache without a full refetch
  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] })
        },
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [user, queryClient])

  const unreadCount = (query.data ?? []).filter((n) => !n.read_at).length

  return { ...query, unreadCount }
}
